/**
 * swipe-indicator.js — mobile swipe affordance for native scroll carousels.
 *
 * Most non-PDP carousels (You may also like, recently viewed, handpicked
 * products, featured collection, product tabs, testimonials, multicolumn,
 * gallery, logo list, …) deactivate Flickity on mobile via its watchCSS hook
 * and fall back to a native CSS scroll-snap track (`.f-swiper`) with the
 * scrollbar hidden. That leaves no hint the row can be swiped.
 *
 * This enhancer injects a compact "‹ current / total ›" control after each
 * `.f-swiper` — mirroring the PDP media counter — and keeps it in sync with the
 * native scroll position. It is purely additive: no per-section markup changes,
 * and it self-attaches to swipers inserted later via AJAX (recommendations,
 * recently viewed). Visibility is mobile-only and driven by whether the track
 * actually overflows; on desktop Flickity's own controls take over and this
 * stays hidden (see overrides.css).
 *
 * The card index is the single source of truth: the counter and the arrow
 * scroll target both derive from it, so an arrow always changes the number by
 * exactly one. Arrow navigation is animated with a requestAnimationFrame tween
 * (not scrollBy({behavior:"smooth"}), which several mobile browsers — notably
 * older iOS Safari — silently ignore). While the arrows drive the scroll the
 * index is not re-derived from scrollLeft; it only follows the shopper's own
 * touch/wheel scrolling. That split keeps arrow steps exactly ±1 even in dense
 * layouts where several trailing cards share the same end scroll position.
 */
(function () {
  "use strict";

  const MOBILE_MQ = "(max-width: 767.98px)";
  const REDUCED_MOTION_MQ = "(prefers-reduced-motion: reduce)";
  const ANIM_DURATION = 350;
  const seen = new WeakSet();
  const instances = new Set();

  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  class SwipeIndicator {
    constructor(scroller) {
      this.scroller = scroller;
      this.frame = null;
      this.animFrame = null;
      this.index = 0;
      // True while the shopper is scrolling by touch/wheel; only then is the
      // counter re-derived from scrollLeft. Arrow navigation sets it false so
      // our own tween never feeds back into the index.
      this.userScrolling = false;

      // Arrow navigation is animated by our own rAF tween for a consistent slide
      // everywhere; disable the CSS smooth scroll so it doesn't fight the tween
      // (touch scrolling is direct manipulation and is unaffected by this).
      this.scroller.style.scrollBehavior = "auto";

      this.build();

      this.onScroll = this.onScroll.bind(this);
      this.onUserScroll = this.onUserScroll.bind(this);
      this.scroller.addEventListener("scroll", this.onScroll, { passive: true });
      this.scroller.addEventListener("touchstart", this.onUserScroll, { passive: true });
      this.scroller.addEventListener("wheel", this.onUserScroll, { passive: true });

      this.refresh();
    }

    // The scroll-snap items. Prefer `.f-column` (the grid cell wrapper used by
    // product/collection carousels); fall back to the track's element children
    // so content sliders that use a different cell class still work. Flickity's
    // own wrapper/counter nodes are never counted.
    get cells() {
      const kids = Array.prototype.filter.call(
        this.scroller.children,
        (el) =>
          el.nodeType === 1 &&
          !el.classList.contains("flickity-page-counter") &&
          !el.classList.contains("flickity-viewport")
      );
      const columns = kids.filter((el) => el.classList.contains("f-column"));
      return columns.length ? columns : kids;
    }

    build() {
      const el = document.createElement("div");
      el.className = "f-swipe-indicator";
      const chevron = (points) =>
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"' +
        ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"' +
        ' focusable="false"><polyline points="' +
        points +
        '"></polyline></svg>';

      el.innerHTML =
        '<div class="f-swipe-indicator__inner">' +
        '<button type="button" class="f-swipe-indicator__arrow f-swipe-indicator__arrow--prev"' +
        ' aria-label="Previous">' +
        chevron("15 18 9 12 15 6") +
        "</button>" +
        '<span class="f-swipe-indicator__counter">' +
        '<span class="f-swipe-indicator__current">1</span>' +
        '<span aria-hidden="true"> / </span>' +
        '<span class="f-swipe-indicator__total">1</span>' +
        "</span>" +
        '<button type="button" class="f-swipe-indicator__arrow f-swipe-indicator__arrow--next"' +
        ' aria-label="Next">' +
        chevron("9 18 15 12 9 6") +
        "</button>" +
        "</div>";

      this.el = el;
      this.currentEl = el.querySelector(".f-swipe-indicator__current");
      this.totalEl = el.querySelector(".f-swipe-indicator__total");
      this.prevBtn = el.querySelector(".f-swipe-indicator__arrow--prev");
      this.nextBtn = el.querySelector(".f-swipe-indicator__arrow--next");

      this.prevBtn.addEventListener("click", () => this.go(-1));
      this.nextBtn.addEventListener("click", () => this.go(1));

      // Sits directly after the track, inside the same (non-bleeding) parent, so
      // it centres under the edge-to-edge carousel.
      this.scroller.insertAdjacentElement("afterend", el);
    }

    // Distance the track advances per card: one card's width plus the gap.
    step() {
      const cells = this.cells;
      if (cells.length > 1) {
        const delta =
          cells[1].getBoundingClientRect().left -
          cells[0].getBoundingClientRect().left;
        if (delta) return Math.abs(delta);
      }
      return cells[0]
        ? cells[0].getBoundingClientRect().width
        : this.scroller.clientWidth;
    }

    // scrollLeft that brings card `index` to the start edge, clamped so we never
    // scroll past the end (in a peek layout the last card can't fully align).
    // Negative under RTL, where the first card sits at the right.
    offsetFor(index) {
      const maxScroll = Math.max(
        this.scroller.scrollWidth - this.scroller.clientWidth,
        0
      );
      const magnitude = Math.min(Math.max(index, 0) * this.step(), maxScroll);
      const rtl = getComputedStyle(this.scroller).direction === "rtl";
      return rtl ? -magnitude : magnitude;
    }

    // Card index inferred from the live scroll position — used to keep the
    // counter in sync while the shopper swipes. Pins to the last card once
    // fully scrolled so the counter can actually read "N / N".
    indexFromScroll() {
      const count = this.cells.length;
      if (count < 2) return 0;
      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
      if (maxScroll <= 0) return 0;
      const position = Math.abs(this.scroller.scrollLeft);
      if (position >= maxScroll - 2) return count - 1;
      return Math.min(Math.max(Math.round(position / this.step()), 0), count - 1);
    }

    onUserScroll() {
      // Hand control to the shopper: stop any running arrow tween and let scroll
      // events drive the index.
      this.userScrolling = true;
      this.cancelAnim();
    }

    go(direction) {
      const count = this.cells.length;
      if (count < 2) return;
      // Arrows own the index now, so ignore scroll feedback from our own tween.
      this.userScrolling = false;
      this.index = Math.min(Math.max(this.index + direction, 0), count - 1);
      this.render(); // reflect the new number/arrow state immediately
      this.animateTo(this.offsetFor(this.index));
    }

    cancelAnim() {
      if (this.animFrame) {
        cancelAnimationFrame(this.animFrame);
        this.animFrame = null;
      }
    }

    animateTo(target) {
      const start = this.scroller.scrollLeft;
      const distance = target - start;
      this.cancelAnim();
      if (Math.abs(distance) < 1) return;
      // Respect reduced-motion: jump straight to the target, no slide.
      if (window.matchMedia(REDUCED_MOTION_MQ).matches) {
        this.scroller.scrollLeft = target;
        return;
      }
      const startTime = performance.now();
      const tick = (now) => {
        const t = Math.min((now - startTime) / ANIM_DURATION, 1);
        this.scroller.scrollLeft = start + distance * easeOutCubic(t);
        if (t < 1) {
          this.animFrame = requestAnimationFrame(tick);
        } else {
          this.animFrame = null;
        }
      };
      this.animFrame = requestAnimationFrame(tick);
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        // Only a shopper's own scrolling moves the counter; our tween does not.
        if (this.userScrolling) this.index = this.indexFromScroll();
        this.render();
      });
    }

    // Recompute the total and whether the control should show at all. Called on
    // init and whenever the viewport changes.
    refresh() {
      const count = this.cells.length;
      this.totalEl.textContent = count;
      const scrollable =
        window.matchMedia(MOBILE_MQ).matches &&
        count > 1 &&
        this.scroller.scrollWidth - this.scroller.clientWidth > 2;
      this.el.classList.toggle("is-visible", scrollable);
      if (scrollable) {
        this.index = this.indexFromScroll();
        this.render();
      }
    }

    render() {
      const count = this.cells.length;
      if (count < 1) return;
      this.index = Math.min(Math.max(this.index, 0), count - 1);
      this.currentEl.textContent = this.index + 1;
      this.totalEl.textContent = count;
      this.prevBtn.disabled = this.index <= 0;
      this.nextBtn.disabled = this.index >= count - 1;
    }

    destroy() {
      this.cancelAnim();
      this.scroller.removeEventListener("scroll", this.onScroll);
      this.scroller.removeEventListener("touchstart", this.onUserScroll);
      this.scroller.removeEventListener("wheel", this.onUserScroll);
      if (this.el && this.el.parentNode) this.el.parentNode.removeChild(this.el);
    }
  }

  function initOne(scroller) {
    if (!(scroller instanceof Element) || seen.has(scroller)) return;
    seen.add(scroller);
    instances.add(new SwipeIndicator(scroller));
  }

  function initAll(root) {
    const scope = root && root.nodeType === 1 ? root : document;
    if (scope.matches && scope.matches(".f-swiper")) initOne(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll(".f-swiper").forEach(initOne);
    }
  }

  // One shared resize handler for every instance; also prunes controls whose
  // track was removed (e.g. recommendations re-rendering) so nothing leaks.
  let resizeFrame = null;
  window.addEventListener("resize", () => {
    if (resizeFrame) return;
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null;
      instances.forEach((instance) => {
        if (!document.contains(instance.scroller)) {
          instance.destroy();
          instances.delete(instance);
          seen.delete(instance.scroller);
        } else {
          instance.refresh();
        }
      });
    });
  });

  function start() {
    initAll(document);

    // Catch swipers inserted after load (product recommendations and recently
    // viewed fetch their markup). Coalesce bursts into one scan per frame.
    let scanQueued = false;
    new MutationObserver((mutations) => {
      if (scanQueued) return;
      if (!mutations.some((m) => m.addedNodes.length)) return;
      scanQueued = true;
      requestAnimationFrame(() => {
        scanQueued = false;
        initAll(document);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
